// The omnisearch dropdown (ortex-gui#1198): instruments, deep links, measures, datasets
// and people, each identifiable at a glance.
//
//   company / ETF / fund   company logo (useCompanyLogo: theme-aware, plate fallback)
//   deep link              company logo + a data badge ("Tesla - Short interest")
//   measure / dataset      data icon
//   person                 portrait from the logo service, else an initials avatar whose
//                          colour is derived from the person, so it is stable
//   index rebalance        the Index Rebalance menu icon; the row opens the index's next
//                          review, and "Overview" opens its reviews on the Index Rebalance page
//
// One ranked list rather than grouped sections: ranking already decides that
// `tsla short interest` leads with the deep link, and grouping by kind would push it down.
// Each row carries a kind label instead. Index Rebalance rows are the exception: TED returns
// them as one block, placed by its best row's score, so "msci world" leads with the index.
//
// Opening a row records the click (lib/omnisearch/click.ts), which feeds ranking, and
// keeps instrument history in user settings exactly as the legacy dropdown does.

import { FC, MouseEventHandler, useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useRecoilState, useRecoilValue } from "recoil";
import { Chip, Typography, useMediaQuery } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import ImageNotSupported from "@mui/icons-material/ImageNotSupported";
import InsightsIcon from "@mui/icons-material/Insights";
import SearchIcon from "@mui/icons-material/Search";
import StorageIcon from "@mui/icons-material/Storage";

import styles from "@/components/header-autocomplete/HeaderAutocomplete.module.scss";
import { menuIconFor } from "@/components/menu/utils";
import { updateUserSettings } from "@/controllers/auth";
import { toFindInstrument } from "@/controllers/find-instruments";
import { useCompanyLogo, useLogosConfig, portraitUrl } from "@/lib/logos";
import { recordSearchClick, SearchSource } from "@/lib/omnisearch/click";
import { OmniResult } from "@/lib/omnisearch/types";
import { getPreferredTabSuffix, readPreferredStockTab } from "@/lib/stock-tabs";
import { backgroundColorState, blackLighterState, greyLighterState, newbackgroundColorState, ortexColorState, searchSettingsState, themeState, userState, whiteDarkerState } from "@/lib/store";
import { classNames } from "@/lib/utils";
import { usExchanges } from "@/lib/vars";
import { Settings } from "@/models/auth";

const INSTRUMENT_KINDS = new Set(["company", "etf", "fund", "index", "fx", "commodity", "reit"]);

const KIND_LABEL: Record<string, string> = {
    company: "Equity",
    etf: "ETF",
    fund: "Fund",
    index: "Index",
    fx: "FX",
    commodity: "Commodity",
    reit: "REIT",
    dataset: "Dataset",
    measure: "Data",
    person: "Person",
    rebalance: "Rebalance",
    deeplink: "Go to",
};

/** Kinds drawn with their page's menu icon rather than a logo or a portrait. */
// A bond (found by its exact ISIN) shows the Bonds menu icon and opens its bond page.
const DATA_KINDS = new Set(["measure", "dataset", "rebalance", "bond"]);

const isInstrument = (result: OmniResult) => INSTRUMENT_KINDS.has(result.kind);

/**
 * Where a row opens. A plain instrument row honours the user's preferred stock tab, the way
 * the legacy dropdown does; a deep link already names its tab.
 */
export const routeForResult = (result: OmniResult, settings: Settings | undefined): string => {
    if (!isInstrument(result)) return result.route;
    const asInstrument = toFindInstrument(result);
    if (!asInstrument) return result.route;
    return `${result.route}${getPreferredTabSuffix(readPreferredStockTab(settings), asInstrument.security_type, asInstrument.exchange)}`;
};

let historyTimeout: NodeJS.Timeout;

const ResultLogo: FC<{ result: OmniResult; theme: string }> = ({ result, theme }) => {
    const logosCfg = useLogosConfig();
    const p = result.payload;
    const logo = useCompanyLogo({ isin: p.isin, ortexSid: p.sid, ticker: p.ticker, exchange: p.exchange, securityId: p.securityId }, "rect");
    const legacyPlate = logosCfg.source === "edge" ? "transparent" : theme === "dark" ? "#c0c0c0" : "transparent";
    if (logo.failed) return <ImageNotSupported />;
    return (
        <div className={styles.logo} style={{ width: "80px", backgroundColor: logo.plate ?? legacyPlate, borderRadius: "4px", position: "relative" }}>
            <Image unoptimized src={logo.src} alt={`${result.title} logo`} layout="fill" objectFit="contain" onError={logo.onError} />
        </div>
    );
};

/** A stable colour per person, so the same initials always look the same. */
const avatarColour = (seed: string): string => {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
    return `hsl(${Math.abs(hash) % 360}, 42%, 42%)`;
};

const initialsOf = (result: OmniResult): string => {
    if (result.payload.initials) return result.payload.initials;
    const parts = result.title.replace(/,/g, " ").split(" ").filter(Boolean);
    if (!parts.length) return "?";
    return (parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const PersonAvatar: FC<{ result: OmniResult }> = ({ result }) => {
    useLogosConfig(); // re-render when the logo service config arrives
    const [failed, setFailed] = useState(false);
    const src = failed ? null : portraitUrl({ portrait: result.payload.portrait }, "square");
    const seed = result.payload.slug || result.key || result.title;
    return (
        <div className={styles.logo} style={{ width: "80px" }}>
            {src ? (
                <Image unoptimized src={src} alt={result.title} width={32} height={32} objectFit="cover" style={{ borderRadius: "50%" }} onError={() => setFailed(true)} />
            ) : (
                <div aria-hidden style={{ width: 32, height: 32, borderRadius: "50%", background: avatarColour(seed), color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 }}>
                    {initialsOf(result)}
                </div>
            )}
        </div>
    );
};

/** The page a measure or deep link opens, e.g. "/analysts" - what the menu keys its icons on. */
const pageOf = (result: OmniResult): string | undefined => {
    if (result.kind === "deeplink") {
        const last = result.route.split("?")[0].split("/").filter(Boolean).pop();
        return last ? `/${last}` : undefined;
    }
    return result.payload.page;
};

/** A measure or dataset shows the same icon as its page in the menu; generic data icons otherwise. */
const DataArt: FC<{ result: OmniResult; colour: string }> = ({ result, colour }) => {
    const icon = menuIconFor(pageOf(result));
    return (
        <div className={styles.logo} style={{ width: "80px", display: "flex", justifyContent: "center", alignItems: "center" }}>
            {icon ? <div className={icon} style={{ color: colour, fontSize: 22 }} /> : result.kind === "dataset" ? <StorageIcon htmlColor={colour} /> : <InsightsIcon htmlColor={colour} />}
        </div>
    );
};

const QUICK_LINKS = [
    { text: "overview", link: "" },
    { text: "shorts", link: "short-interest" },
    { text: "analysts", link: "analysts" },
    { text: "options", link: "options" },
    { text: "signals", link: "trading-signals" },
    { text: "scores", link: "stock-scores" },
];
const ETF_QUICK_LINKS = [
    { text: "overview", link: "" },
    { text: "corp actions", link: "corporate-actions" },
    { text: "options", link: "options" },
    { text: "shorts", link: "short-interest" },
    { text: "signals", link: "trading-signals" },
];

interface PropsTypes {
    results: OmniResult[];
    activeIndex?: number;
    /** The query the results answer, recorded with a click. */
    query: string;
    source: SearchSource;
    onMouseEnter?: MouseEventHandler<HTMLDivElement>;
    /** Called after a row is opened, so the host can close the box. */
    onSelect?: () => void;
}

const OmniAutocomplete: FC<PropsTypes> = ({ results, activeIndex, query, source, onMouseEnter, onSelect }) => {
    const router = useRouter();
    const theme = useRecoilValue(themeState);
    const blackLighter = useRecoilValue(blackLighterState);
    const whiteDarker = useRecoilValue(whiteDarkerState);
    const greyLighter = useRecoilValue(greyLighterState);
    const ortexColor = useRecoilValue(ortexColorState);
    const backgroundColor = useRecoilValue(backgroundColorState);
    const newbackgroundColor = useRecoilValue(newbackgroundColorState);
    const [user, setUser] = useRecoilState(userState);
    const searchSettings = useRecoilValue(searchSettingsState);
    const isMobile = useMediaQuery("(max-width: 900px)");
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        if (activeIndex !== null && activeIndex !== undefined && itemRefs.current[activeIndex]) {
            itemRefs.current[activeIndex].scrollIntoView({ block: "nearest" });
        }
    }, [activeIndex]);

    /** Same history rule as the legacy dropdown: the last five instruments opened, by name. */
    const rememberInstrument = (result: OmniResult) => {
        const asInstrument = result.kind === "deeplink" || isInstrument(result) ? toFindInstrument({ ...result, kind: result.kind === "deeplink" ? "company" : result.kind }) : null;
        if (!asInstrument) return;
        const merged = [...(searchSettings || []), { ...asInstrument, name: result.kind === "deeplink" ? asInstrument.name.split(" - ")[0] : asInstrument.name }];
        const search_settings = merged.filter((value, index, self) => index === self.findIndex((item) => item.name === value.name));
        if (search_settings.length > 5) search_settings.splice(0, search_settings.length - 5);
        const settings: Settings = { ...user.settings, search_settings };
        setUser({ ...user, settings });
        if (process.env.NEXT_PUBLIC_VERCEL_ENV && user.subscription_plan !== "ORTEX Visitor") {
            clearTimeout(historyTimeout);
            historyTimeout = setTimeout(() => updateUserSettings(settings), 1000);
        }
    };

    const open = (result: OmniResult, index: number, route?: string) => {
        recordSearchClick(result, query, index, source);
        rememberInstrument(result);
        router.push(route ?? routeForResult(result, user?.settings));
        onSelect?.();
    };

    /** An index's two ways in: its next (or latest) review, which the row itself opens, and its overview. */
    const rebalanceLinks = (result: OmniResult, index: number) => {
        // The second line names the provider; the review ("next: November review, announced 11 Nov") is the first link.
        const review = result.subtitle.split(" · ").pop() || (result.payload.upcoming === false ? "latest review" : "next review");
        const links = [{ text: review, href: result.route }, ...(result.payload.overview ? [{ text: "overview", href: result.payload.overview }] : [])];
        return links.map((l) => (
            <Link key={l.text} href={l.href} passHref>
                <a
                    style={{ color: greyLighter }}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        open(result, index, l.href);
                    }}
                >
                    {l.text.toUpperCase()}
                </a>
            </Link>
        ));
    };

    const quickLinks = (result: OmniResult, index: number) => {
        const asInstrument = toFindInstrument(result);
        if (!asInstrument) return null;
        const links = (asInstrument.security_type === "ETF" ? ETF_QUICK_LINKS : QUICK_LINKS).filter((l) => l.text !== "options" || usExchanges.includes(asInstrument.exchange));
        return links.map((l) => {
            const href = `${result.route}${l.link ? `/${l.link}` : ""}`;
            return (
                <Link key={l.text} href={href} passHref>
                    <a
                        style={{ color: greyLighter }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            open(result, index, href);
                        }}
                    >
                        {l.text.toUpperCase()}
                    </a>
                </Link>
            );
        });
    };

    const art = (result: OmniResult) => {
        if (result.kind === "person") return <PersonAvatar result={result} />;
        if (DATA_KINDS.has(result.kind)) return <DataArt result={result} colour={ortexColor} />;
        return (
            <div style={{ position: "relative" }}>
                <ResultLogo result={result} theme={theme} />
                {result.kind === "deeplink" && (
                    <span style={{ position: "absolute", right: 6, bottom: -6, background: backgroundColor, borderRadius: "50%", display: "flex" }}>
                        {menuIconFor(pageOf(result)) ? <div className={menuIconFor(pageOf(result))} style={{ color: ortexColor, fontSize: 13, padding: 2 }} /> : <InsightsIcon htmlColor={ortexColor} sx={{ fontSize: 16 }} />}
                    </span>
                )}
            </div>
        );
    };

    const heading = (result: OmniResult) => {
        if (isInstrument(result)) return `${result.payload.exchange}:${result.payload.ticker}`;
        return result.title;
    };

    const detail = (result: OmniResult) => {
        if (isInstrument(result)) return result.title;
        if (result.kind === "rebalance") return result.payload.provider || "Index Rebalance";
        return result.subtitle;
    };

    return (
        <div className={styles.HeaderAutocomplete} style={{ display: results.length ? "block" : "none", top: isMobile ? "calc(100px + env(safe-area-inset-top) + 26px)" : "85px" }} onMouseEnter={onMouseEnter}>
            <style jsx>{`
                .${styles.instrument}:hover, .${styles.instrument}.active {
                    background-color: ${theme === "dark" ? blackLighter : whiteDarker} !important;
                    cursor: pointer;
                }
            `}</style>
            <div className={styles.instrumentsContainer}>
                {results.map((result, index) => (
                    <div
                        key={`${result.id}-${index}`}
                        ref={(el) => (itemRefs.current[index] = el)}
                        role="option"
                        aria-selected={index === activeIndex}
                        className={classNames(styles.instrument, index === activeIndex && "active")}
                        style={{ borderBottom: `1px solid ${whiteDarker}`, backgroundColor: theme === "dark" ? backgroundColor : newbackgroundColor }}
                        onClick={() => open(result, index)}
                    >
                        <div className={styles.instrumentWrapper}>
                            {result.id.startsWith("history:") ? <HistoryIcon htmlColor={greyLighter} fontSize="medium" sx={{ mr: isMobile ? 1 : 3 }} /> : <SearchIcon htmlColor={greyLighter} fontSize="medium" sx={{ mr: isMobile ? 1 : 3 }} />}
                            {art(result)}
                            <div className={styles.instrumentContent}>
                                <div className={styles.instrumentInfo}>
                                    <Typography sx={{ fontWeight: 700 }} className={styles.ticker}>
                                        {heading(result)}
                                    </Typography>
                                    {/* A long fund name truncates; the listing count never wraps under it. */}
                                    <Typography variant="body2" className={styles.name} title={detail(result)} sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {detail(result)}
                                    </Typography>
                                    {result.otherListings > 0 && (
                                        <Typography component="span" variant="caption" sx={{ mr: 1, opacity: 0.7, whiteSpace: "nowrap", flexShrink: 0 }}>
                                            +{result.otherListings} listing{result.otherListings === 1 ? "" : "s"}
                                        </Typography>
                                    )}
                                    <div className={styles.type}>
                                        <Chip
                                            className={styles.securityType}
                                            label={KIND_LABEL[result.kind] ?? result.kind}
                                            color={result.kind === "etf" ? "secondary" : result.kind === "person" || DATA_KINDS.has(result.kind) || result.kind === "deeplink" ? "success" : "info"}
                                            variant="outlined"
                                            size="small"
                                        />
                                    </div>
                                </div>
                                {isInstrument(result) && !isMobile && <div className={styles.instrumentLinks}>{quickLinks(result, index)}</div>}
                                {result.kind === "rebalance" && <div className={styles.instrumentLinks}>{rebalanceLinks(result, index)}</div>}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default OmniAutocomplete;
